import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2 } from 'lucide-react';
import type { Target, TargetSpec, VideoFormatSpec } from '@shared/protocol';
import { api } from '../lib/api';
import { useDocumentTitle } from '../lib/branding';
import { reloadTargets, useTargets } from '../lib/useTargets';
import { Notice } from '../components/Status';

const blankSpec = (): TargetSpec => ({
  loudness: { integrated: -23, tolerance: 1, maxTruePeak: -1, maxShortTerm: null, maxLra: null },
  silence: { thresholdDb: -60, seconds: 10 },
  phase: { correlationThreshold: -0.3, seconds: 5 },
  audio: { codecs: null, sampleRates: [48000], channels: null, trackCount: null, bitrateMinKbps: null, bitrateMaxKbps: null },
  video: { codecs: null, profiles: null, formats: null, scan: null, fieldOrder: null, colorSpace: null, colorRange: null, keyframeIntervalMax: null, rateControl: null },
  srt: { maxLossPercent: 0.5, maxRttMs: 300, minBufferMs: 80 },
});

/** Text field for an optional list ("aac, mp3"); empty means "any". */
const listText = (v: (string | number)[] | null) => (v ? v.join(', ') : '');
const parseList = (s: string): string[] | null => {
  const l = s.split(',').map((x) => x.trim()).filter(Boolean);
  return l.length ? l : null;
};
const parseNums = (s: string): number[] | null => {
  const l = parseList(s);
  if (!l) return null;
  const n = l.map(Number);
  if (n.some((x) => !Number.isFinite(x))) throw new Error(`"${s}" must be a comma-separated list of numbers.`);
  return n;
};
const numOrNull = (s: string): number | null => (s.trim() === '' ? null : Number(s));

function Num({ id, label, value, onChange, hint, step = 'any' }: { id: string; label: string; value: number | null; onChange: (v: number | null) => void; hint?: string; step?: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input id={id} className="input mono" type="number" step={step} value={value ?? ''} onChange={(e) => onChange(numOrNull(e.target.value))} placeholder="any" />
      {hint && <span className="hint">{hint}</span>}
    </div>
  );
}

export function PresetEditPage() {
  const params = useParams();
  const editId = params.id && params.id !== 'new' ? Number(params.id) : null;
  useDocumentTitle(editId ? 'Edit preset' : 'New preset');
  const navigate = useNavigate();
  const targets = useTargets();
  const existing = editId ? targets.find((t) => t.id === editId) : null;
  const [kind, setKind] = useState<'broadcaster' | 'platform'>('broadcaster');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sources, setSources] = useState('');
  const [checkedAt, setCheckedAt] = useState('');
  const [spec, setSpec] = useState<TargetSpec>(blankSpec());
  const [lists, setLists] = useState({ aCodecs: '', aRates: '48000', aChannels: '', vCodecs: '', vProfiles: '' });
  const [error, setError] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(!editId);

  useEffect(() => {
    if (!existing || loaded) return;
    setKind(existing.kind);
    setName(existing.name);
    setDescription(existing.description);
    setSources(existing.sourceUrls.join('\n'));
    setCheckedAt(existing.checkedAt ?? '');
    setSpec(structuredClone(existing.spec));
    setLists({
      aCodecs: listText(existing.spec.audio.codecs),
      aRates: listText(existing.spec.audio.sampleRates),
      aChannels: listText(existing.spec.audio.channels),
      vCodecs: listText(existing.spec.video.codecs),
      vProfiles: listText(existing.spec.video.profiles),
    });
    setLoaded(true);
  }, [existing, loaded]);

  const set = <K extends keyof TargetSpec>(k: K, v: TargetSpec[K]) => setSpec((s) => ({ ...s, [k]: v }));
  const setFormat = (i: number, patch: Partial<VideoFormatSpec>) => set('video', { ...spec.video, formats: (spec.video.formats ?? []).map((f, j) => (i === j ? { ...f, ...patch } : f)) });

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const body: Omit<Target, 'id' | 'builtin' | 'updatedAt'> = {
        kind,
        name,
        description,
        sourceUrls: sources.split(/\s+/).filter(Boolean),
        checkedAt: checkedAt || null,
        spec: {
          ...spec,
          audio: { ...spec.audio, codecs: parseList(lists.aCodecs), sampleRates: parseNums(lists.aRates), channels: parseNums(lists.aChannels) },
          video: { ...spec.video, codecs: parseList(lists.vCodecs), profiles: parseList(lists.vProfiles), formats: spec.video.formats?.length ? spec.video.formats : null },
        },
      };
      if (editId) await api('PUT', `/api/targets/${editId}`, body);
      else await api('POST', '/api/targets', body);
      await reloadTargets();
      navigate('/presets');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
    }
  };

  const remove = async () => {
    if (!editId || !confirm(`Delete "${name}"? Streams using it will no longer be checked against it.`)) return;
    try {
      await api('DELETE', `/api/targets/${editId}`);
      await reloadTargets();
      navigate('/presets');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete.');
    }
  };

  if (!loaded) return <div className="page narrow">{targets.length ? <Notice level="fail">Preset not found.</Notice> : <p className="muted">Loading</p>}</div>;
  const L = spec.loudness;

  return (
    <div className="page narrow">
      <div className="page-header">
        <div>
          <Link to="/presets" className="back-link">
            <ArrowLeft size={14} aria-hidden /> Presets and profiles
          </Link>
          <h1>{editId ? `Edit ${existing?.name ?? ''}` : 'New preset or profile'}</h1>
          {existing?.builtin && <p className="muted small">A built-in entry: your edits are kept across upgrades.</p>}
        </div>
      </div>
      <form onSubmit={submit} className="stream-form">
        <section className="card">
          <header className="card-header">
            <h2>General</h2>
          </header>
          <div className="card-body form-grid">
            <div className="field">
              <label htmlFor="p-name">Name</label>
              <input id="p-name" className="input" value={name} onChange={(e) => setName(e.target.value)} required maxLength={120} />
            </div>
            <div className="field">
              <label htmlFor="p-kind">Kind</label>
              <select id="p-kind" className="input" value={kind} onChange={(e) => setKind(e.target.value as 'broadcaster' | 'platform')}>
                <option value="broadcaster">Broadcaster preset</option>
                <option value="platform">Platform profile</option>
              </select>
            </div>
            <div className="field field-wide">
              <label htmlFor="p-desc">Description</label>
              <textarea id="p-desc" className="input" value={description} onChange={(e) => setDescription(e.target.value)} maxLength={4000} />
            </div>
            <div className="field field-wide">
              <label htmlFor="p-src">Source URLs (one per line)</label>
              <textarea id="p-src" className="input mono" value={sources} onChange={(e) => setSources(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="p-date">Date checked</label>
              <input id="p-date" className="input" type="date" value={checkedAt} onChange={(e) => setCheckedAt(e.target.value)} />
            </div>
          </div>
        </section>

        <section className="card">
          <header className="card-header">
            <h2>Loudness</h2>
            <label className="checkbox">
              <input type="checkbox" checked={L !== null} onChange={(e) => set('loudness', e.target.checked ? blankSpec().loudness : null)} /> Check loudness
            </label>
          </header>
          {L && (
            <div className="card-body form-grid">
              <Num id="l-int" label="Integrated target (LUFS)" value={L.integrated} onChange={(v) => set('loudness', { ...L, integrated: v ?? -23 })} />
              <Num id="l-tol" label="Tolerance (LU)" value={L.tolerance} onChange={(v) => set('loudness', { ...L, tolerance: v ?? 1 })} />
              <Num id="l-tp" label="Maximum true peak (dBTP)" value={L.maxTruePeak} onChange={(v) => set('loudness', { ...L, maxTruePeak: v ?? -1 })} />
              <Num id="l-st" label="Maximum short-term (LUFS)" value={L.maxShortTerm} onChange={(v) => set('loudness', { ...L, maxShortTerm: v })} />
              <Num id="l-lra" label="Maximum LRA (LU)" value={L.maxLra} onChange={(v) => set('loudness', { ...L, maxLra: v })} />
            </div>
          )}
        </section>

        <section className="card">
          <header className="card-header">
            <h2>Audio</h2>
          </header>
          <div className="card-body form-grid">
            <Num id="s-thr" label="Silence threshold (dBFS)" value={spec.silence.thresholdDb} onChange={(v) => set('silence', { ...spec.silence, thresholdDb: v ?? -60 })} />
            <Num id="s-sec" label="Silence duration (s)" value={spec.silence.seconds} onChange={(v) => set('silence', { ...spec.silence, seconds: v ?? 10 })} />
            <Num id="ph-thr" label="Phase correlation threshold" value={spec.phase.correlationThreshold} onChange={(v) => set('phase', { ...spec.phase, correlationThreshold: v ?? -0.3 })} />
            <Num id="ph-sec" label="Phase duration (s)" value={spec.phase.seconds} onChange={(v) => set('phase', { ...spec.phase, seconds: v ?? 5 })} />
            <div className="field">
              <label htmlFor="a-codecs">Audio codecs</label>
              <input id="a-codecs" className="input mono" value={lists.aCodecs} onChange={(e) => setLists({ ...lists, aCodecs: e.target.value })} placeholder="any (e.g. aac, mp3)" />
            </div>
            <div className="field">
              <label htmlFor="a-rates">Sample rates (Hz)</label>
              <input id="a-rates" className="input mono" value={lists.aRates} onChange={(e) => setLists({ ...lists, aRates: e.target.value })} placeholder="any (e.g. 48000)" />
            </div>
            <div className="field">
              <label htmlFor="a-ch">Channel counts</label>
              <input id="a-ch" className="input mono" value={lists.aChannels} onChange={(e) => setLists({ ...lists, aChannels: e.target.value })} placeholder="any (e.g. 2, 6)" />
            </div>
            <Num id="a-tracks" label="Audio tracks" value={spec.audio.trackCount} onChange={(v) => set('audio', { ...spec.audio, trackCount: v })} step="1" />
            <Num id="a-min" label="Audio bitrate min (kb/s)" value={spec.audio.bitrateMinKbps} onChange={(v) => set('audio', { ...spec.audio, bitrateMinKbps: v })} />
            <Num id="a-max" label="Audio bitrate max (kb/s)" value={spec.audio.bitrateMaxKbps} onChange={(v) => set('audio', { ...spec.audio, bitrateMaxKbps: v })} />
          </div>
        </section>

        <section className="card">
          <header className="card-header">
            <h2>Video</h2>
          </header>
          <div className="card-body">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="v-codecs">Codecs</label>
                <input id="v-codecs" className="input mono" value={lists.vCodecs} onChange={(e) => setLists({ ...lists, vCodecs: e.target.value })} placeholder="any (e.g. h264, hevc)" />
              </div>
              <div className="field">
                <label htmlFor="v-prof">Profiles</label>
                <input id="v-prof" className="input mono" value={lists.vProfiles} onChange={(e) => setLists({ ...lists, vProfiles: e.target.value })} placeholder="any (e.g. High, Main)" />
              </div>
              <div className="field">
                <label htmlFor="v-scan">Scan</label>
                <select id="v-scan" className="input" value={spec.video.scan ?? ''} onChange={(e) => set('video', { ...spec.video, scan: (e.target.value || null) as TargetSpec['video']['scan'] })}>
                  <option value="">Any</option>
                  <option value="progressive">Progressive</option>
                  <option value="interlaced">Interlaced</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="v-fo">Field order</label>
                <select id="v-fo" className="input" value={spec.video.fieldOrder ?? ''} onChange={(e) => set('video', { ...spec.video, fieldOrder: (e.target.value || null) as TargetSpec['video']['fieldOrder'] })}>
                  <option value="">Any</option>
                  <option value="tff">Top field first</option>
                  <option value="bff">Bottom field first</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="v-cs">Colour space</label>
                <select id="v-cs" className="input" value={spec.video.colorSpace ?? ''} onChange={(e) => set('video', { ...spec.video, colorSpace: e.target.value || null })}>
                  <option value="">Any</option>
                  <option value="bt709">BT.709</option>
                  <option value="bt2020nc">BT.2020</option>
                  <option value="smpte170m">BT.601 (525)</option>
                  <option value="bt470bg">BT.601 (625)</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="v-range">Colour range</label>
                <select id="v-range" className="input" value={spec.video.colorRange ?? ''} onChange={(e) => set('video', { ...spec.video, colorRange: (e.target.value || null) as TargetSpec['video']['colorRange'] })}>
                  <option value="">Any</option>
                  <option value="tv">Limited (TV)</option>
                  <option value="pc">Full (PC)</option>
                </select>
              </div>
              <Num id="v-kf" label="Maximum keyframe interval (s)" value={spec.video.keyframeIntervalMax} onChange={(v) => set('video', { ...spec.video, keyframeIntervalMax: v })} />
              <div className="field">
                <label htmlFor="v-rc">Rate control</label>
                <select id="v-rc" className="input" value={spec.video.rateControl ?? ''} onChange={(e) => set('video', { ...spec.video, rateControl: (e.target.value || null) as TargetSpec['video']['rateControl'] })}>
                  <option value="">Any</option>
                  <option value="CBR">CBR</option>
                  <option value="VBR">VBR</option>
                </select>
              </div>
            </div>
            <h4>Formats and bitrates</h4>
            <p className="muted small">Accepted resolutions with their frame rates and recommended video bitrate range. Leave empty to accept any.</p>
            <table className="table table-compact">
              <thead>
                <tr>
                  <th>Width</th>
                  <th>Height</th>
                  <th>Frame rates</th>
                  <th>Min kb/s</th>
                  <th>Max kb/s</th>
                  <th>
                    <span className="visually-hidden">Remove</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(spec.video.formats ?? []).map((f, i) => (
                  <tr key={i}>
                    <td>
                      <input className="input input-inline input-narrow mono" aria-label="Width" type="number" value={f.width} onChange={(e) => setFormat(i, { width: Number(e.target.value) })} />
                    </td>
                    <td>
                      <input className="input input-inline input-narrow mono" aria-label="Height" type="number" value={f.height} onChange={(e) => setFormat(i, { height: Number(e.target.value) })} />
                    </td>
                    <td>
                      <input
                        className="input input-inline mono"
                        aria-label="Frame rates"
                        defaultValue={f.frameRates.join(', ')}
                        onBlur={(e) => {
                          try {
                            setFormat(i, { frameRates: parseNums(e.target.value) ?? [] });
                          } catch (err) {
                            setError((err as Error).message);
                          }
                        }}
                      />
                    </td>
                    <td>
                      <input className="input input-inline input-narrow mono" aria-label="Minimum bitrate" type="number" value={f.bitrateMinKbps ?? ''} onChange={(e) => setFormat(i, { bitrateMinKbps: numOrNull(e.target.value) })} />
                    </td>
                    <td>
                      <input className="input input-inline input-narrow mono" aria-label="Maximum bitrate" type="number" value={f.bitrateMaxKbps ?? ''} onChange={(e) => setFormat(i, { bitrateMaxKbps: numOrNull(e.target.value) })} />
                    </td>
                    <td>
                      <button type="button" className="btn btn-sm btn-ghost btn-icon btn-danger" aria-label="Remove format" onClick={() => set('video', { ...spec.video, formats: (spec.video.formats ?? []).filter((_, j) => j !== i) })}>
                        <Trash2 size={14} aria-hidden />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="btn btn-sm" onClick={() => set('video', { ...spec.video, formats: [...(spec.video.formats ?? []), { width: 1920, height: 1080, frameRates: [25], bitrateMinKbps: null, bitrateMaxKbps: null }] })}>
              <Plus size={14} aria-hidden /> Add format
            </button>
          </div>
        </section>

        <section className="card">
          <header className="card-header">
            <h2>SRT delivery</h2>
            <label className="checkbox">
              <input type="checkbox" checked={spec.srt !== null} onChange={(e) => set('srt', e.target.checked ? blankSpec().srt : null)} /> Check SRT thresholds
            </label>
          </header>
          {spec.srt && (
            <div className="card-body form-grid">
              <Num id="srt-loss" label="Maximum packet loss (%)" value={spec.srt.maxLossPercent} onChange={(v) => set('srt', { ...spec.srt!, maxLossPercent: v ?? 0.5 })} />
              <Num id="srt-rtt" label="Maximum RTT (ms)" value={spec.srt.maxRttMs} onChange={(v) => set('srt', { ...spec.srt!, maxRttMs: v ?? 300 })} />
              <Num id="srt-buf" label="Minimum receive buffer (ms)" value={spec.srt.minBufferMs} onChange={(v) => set('srt', { ...spec.srt!, minBufferMs: v ?? 80 })} />
            </div>
          )}
        </section>

        {error && <Notice level="fail">{error}</Notice>}
        <div className="form-actions">
          {editId && !existing?.builtin && (
            <button type="button" className="btn btn-danger" onClick={() => void remove()} style={{ marginRight: 'auto' }}>
              <Trash2 size={14} aria-hidden /> Delete
            </button>
          )}
          <Link className="btn" to="/presets">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary">
            Save
          </button>
        </div>
      </form>
    </div>
  );
}
